import React, { Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { useMediaQuery } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";
import { Button } from "react-bootstrap";
import LoaderInternal from "../../LoaderInternal";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/splide/dist/css/splide.min.css";
import { fetchPackageDetails } from "../../data/packages/packageDetailsSlice";
import { fetchPackagePrice } from "../../data/packages/packagePriceSlice";
import { useTranslation } from "react-i18next";
import { ru, ar, pt, es } from "date-fns/locale";
SwiperCore.use([Navigation, Pagination, Autoplay]);
const PackageDetails = () => {
  const { t, i18n } = useTranslation();
  const getLocale = () => {
    switch (i18n.language) {
      case "ru":
        return ru;
      case "pt":
        return pt;
      case "es":
        return es;
      case "ar":
        return ar;
      default:
        return undefined; // Default to English if no match
    }
  };
  const { productUrl } = useParams();
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(max-width:1024px)");
  const currency = useSelector((state) => state.currency.currency);
  const language = useSelector((state) => state.language.language);
  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const { packageDetails, loading, error } = useSelector(
    (state) => state.packageDetails
  );
  useEffect(() => {
    dispatch(fetchPackageDetails({ productUrl, currency, language }));
  }, [dispatch, productUrl, currency, language]);

  const start1 = packageDetails.packageDetails?.bookingStartDate
    ? new Date(packageDetails.packageDetails.bookingStartDate)
    : null;
  const end1 = packageDetails.packageDetails?.bookingEndDate
    ? new Date(packageDetails.packageDetails.bookingEndDate)
    : null;

  const availableDaysIndex = !packageDetails.packageDetails?.isGroupDeparture
    ? packageDetails.packageDetails?.productEnableDays?.map((day) => {
        switch (day) {
          case "Sun":
            return 0;
          case "Mon":
            return 1;
          case "Tue":
            return 2;
          case "Wed":
            return 3;
          case "Thr":
            return 4;
          case "Fri":
            return 5;
          case "Sat":
            return 6;
          default:
            return -1;
        }
      })
    : [];
  const fixedGroupAvailableDates = packageDetails.packageDetails
    ?.isGroupDeparture
    ? packageDetails.packageDetails?.fixedGroupAvailableDates?.map(
        (date) => new Date(date)
      )
    : [];
  const isDateAvailable = (date) => {
    if (!packageDetails.packageDetails?.isGroupDeparture) {
      const dayIndex = date.getDay();
      return (
        date >= start1 && date <= end1 && availableDaysIndex.includes(dayIndex)
      );
    } else {
      return fixedGroupAvailableDates.some(
        (availableDate) =>
          availableDate.getFullYear() === date.getFullYear() &&
          availableDate.getMonth() === date.getMonth() &&
          availableDate.getDate() === date.getDate()
      );
    }
  };
  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };
  const findFirstAvailableDate = () => {
    if (!start1 || !end1) return null;

    if (packageDetails.packageDetails?.isGroupDeparture) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return fixedGroupAvailableDates.find((date) => date >= today) || null;
    } else {
      let date = addDays(new Date(), packageDetails.packageDetails?.cutOfDays); // Start from today
      date.setHours(0, 0, 0, 0); // Ensure time is set to 00:00:00
      while (date <= end1) {
        if (isDateAvailable(date)) {
          return date;
        }
        date.setDate(date.getDate() + 1);
      }
      return null;
    }
  };
  const getMinDate = () => {
    if (packageDetails.packageDetails?.isGroupDeparture) {
      return fixedGroupAvailableDates.length > 0
        ? fixedGroupAvailableDates.reduce((a, b) => (a < b ? a : b))
        : null;
    } else {
      return addDays(new Date(), packageDetails.packageDetails?.cutOfDays);
    }
  };

  const getMaxDate = () => {
    if (packageDetails.packageDetails?.isGroupDeparture) {
      return fixedGroupAvailableDates.length > 0
        ? fixedGroupAvailableDates.reduce((a, b) => (a > b ? a : b))
        : null;
    } else {
      return end1;
    }
  };

  const [startDate, setStartDate] = useState(findFirstAvailableDate());
  const [minDate, setMinDate] = useState(getMinDate());
  const [maxDate, setMaxDate] = useState(getMaxDate());

  useEffect(() => {
    setStartDate(findFirstAvailableDate());
    setMinDate(getMinDate());
    setMaxDate(getMaxDate());
  }, [packageDetails, packageDetails.packageDetails?.isGroupDeparture]);

  const productId = packageDetails.packageDetails?.productId;

  const [numberOfchildrens1, setNumberOfchildrens1] = useState(0);
  const [numberOfchildrens2, setNumberOfchildrens2] = useState(0);
  const [roomType, setRoomType] = useState(1);
  const [roomNumber, setRoomNumber] = useState(1);
  const { packagePrice } = useSelector((state) => state.packagePrice);

  useEffect(() => {
    if ((productId, startDate)) {
      dispatch(
        fetchPackagePrice({
          productId,
          startDate: formatDate(startDate),
          numberOfchildrens1,
          numberOfchildrens2,
          roomType,
          roomNumber,
          currency,
        })
      );
    }
  }, [
    dispatch,
    productId,
    numberOfchildrens1,
    currency,
    numberOfchildrens2,
    roomType,
    roomNumber,
    startDate,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();

    localStorage.setItem("package_productId", productId);
    localStorage.setItem("package_numberOfchildrens1", numberOfchildrens1);
    localStorage.setItem("package_numberOfchildrens2", numberOfchildrens2);
    localStorage.setItem("package_roomType", roomType);
    localStorage.setItem("package_roomNumber", roomNumber);
    localStorage.setItem("package_startDate", formatDate(startDate));

    navigate(`bookingPackage`);
  };
  const [activeTab, setActiveTab] = useState("rlr-product-sec-overview");
  const [showPopover, setShowPopover] = useState(false);

  const togglePopover = () => {
    setShowPopover(!showPopover);
  };
  // slider
  const [mainSplide, setMainSplide] = useState(null);
  const [thumbnailSplide, setThumbnailSplide] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [totalSlides, setTotalSlides] = useState(
    packageDetails.packagePhotos?.length
  );

  useEffect(() => {
    if (mainSplide && thumbnailSplide) {
      // Update main carousel when a thumbnail is clicked
      thumbnailSplide.on("click", (slide) => {
        const index = slide.index;
        mainSplide.go(index); // Synchronize main carousel with thumbnail click
      });

      // Update currentIndex and totalSlides on main carousel change
      mainSplide.on("moved", () => {
        const index = mainSplide.index;
        setCurrentIndex(index + 1); // Splide uses zero-based index
        setTotalSlides(mainSplide.length);
      });
    }
  }, [mainSplide, thumbnailSplide]);
  // Effect to initialize Splide instances and synchronize them
  useEffect(() => {
    if (mainSplide && thumbnailSplide) {
      // Update main carousel when a thumbnail is clicked
      thumbnailSplide.on("click", (slide) => {
        const index = slide.index;
        mainSplide.go(index); // Synchronize main carousel with thumbnail click
      });
    }
  }, [mainSplide, thumbnailSplide]);

  // Main carousel options
  const mainOptions = {
    type: "loop",
    perPage: 1,
    pagination: false,
    height: isMobile ? "50" : "75vh",
    drag: true,
    gap: 10,
    updateOnMove: true,
  };

  // Thumbnail carousel options
  const thumbnailOptions = {
    fixedWidth: 180,
    fixedHeight: 122,
    isNavigation: false,
    focus: "center",
    pagination: false,
    direction: "ttb",
    height: "75vh",
    gap: 10,
  };

  return (
    <>
      <Helmet>
        <title>{packageDetails.pageDetails?.pageMetatagTitle}</title>

        <link
          rel="canonical"
          href={`https://travco.com/${language}/packages/${productUrl}`}
        />
        <meta
          name="description"
          content={packageDetails.pageDetails?.pageMetatagDescription}
        />
        <meta
          property="og:title"
          content={packageDetails.pageDetails?.metatagTitleFacebook}
        />
        <meta
          property="og:description"
          content={packageDetails.pageDetails?.metatagDescriptionFacebook}
        />
        <meta
          name="twitter:title"
          content={packageDetails.pageDetails?.metatagTitleTwitter}
        />
        <meta
          name="twitter:description"
          content={packageDetails.pageDetails?.metatagDescriptionTwitter}
        />
        <style type="text/css">
          {`
          .react-datepicker-wrapper{
          width:100%
          }
          .react-datepicker__day, .react-datepicker__month-text, .react-datepicker__quarter-text, .react-datepicker__year-text{
          background-color:#ddd;
          border-radius:5px;
          }
          .react-datepicker__day--disabled, .react-datepicker__month-text--disabled, .react-datepicker__quarter-text--disabled, .react-datepicker__year-text--disabled{
          background-color:#fff;}

          `}
        </style>
      </Helmet>
      <section className="outter">
        <div
          id="rlr_banner_slider"
          className="splide rlr-banner-splide rlr-banner-splide--fullwidth-v2"
        >
          <div className="splide__track rlr-banner-splide__track">
            <ul className="splide__list height-internal-pages">
              <li className="splide__slide height-internal-pages rlr-banner-splide__slide">
                <div className="rlr-banner-splide__image-wrapper height-internal-pages">
                  <div className="overlay-banner"></div>
                  <Suspense fallback={<div>{t("Loading")}...</div>}>
                    <img
                      src={
                        isMobile
                          ? packageDetails.pageDetails?.pageBannerMobile
                          : isTablet
                          ? packageDetails.pageDetails?.pageBannerTablet
                          : packageDetails.pageDetails?.pageBannerPc
                      }
                      alt={packageDetails.pageDetails?.pageTitle}
                      loading="lazy"
                    />
                  </Suspense>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>
      <div className="container">
        <div className="rlr-search-results-page__breadcrumb-section">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb rlr-breadcrumb__items">
              <li className="breadcrumb-item rlr-breadcrumb__item">
                <Link to="/">{t("Home")} </Link>
              </li>
              <li className="breadcrumb-item rlr-breadcrumb__item">
                <Link to="/packages">{t("Packages")} </Link>
              </li>

              <li
                className="breadcrumb-item rlr-breadcrumb__item active"
                aria-current="page"
              >
                {packageDetails.pageDetails?.pageTitle}
              </li>
            </ol>
          </nav>
          <div className="rlr-icon-text">
            <i className="rlr-icon-font flaticon-phone-receiver-silhouette">
              {" "}
            </i>{" "}
            <Link
              to="tel:(+202) 3854 1010"
              className="rlr-search-results-page__phone"
            >
              {t("Questions")}{" "}
              {language == "ar" ? (
                <>
                  ؟ <span style={{ direction: "ltr" }}> (+202) 3854 1010</span>
                </>
              ) : (
                "? (+202) 3854 1010"
              )}{" "}
            </Link>
          </div>
        </div>
      </div>
      {/* Slides  */}
      {loading ? (
        <LoaderInternal />
      ) : (
        <section className="py-5 mt-lg-5">
          <div className="container">
            <aside className="row">
              <div className="rlr-product-detail-header__contents d-flex justify-content-between px-3">
                <h1 className="rlr-section__heading--main rlr-product-detail-header__title rlr-section__title--main">
                  {packageDetails.pageDetails?.pageTitle}{" "}
                </h1>{" "}
                <div
                  className="rlr-product-detail-header d-inline-block "
                  id="rlr-js-detail-header"
                >
                  <div className="rlr-product-detail-header__actions">
                    <button
                      type="button"
                      onClick={togglePopover}
                      id="rlr-js-share-button"
                      className="btn rlr-button rlr-button--circle rlr-popover-button  "
                      aria-label="share"
                    >
                      <i className="rlr-icon-font flaticon-share-1"></i>
                    </button>
                    <div
                      id="rlr-js-share-popover"
                      className={`rlr-popover ${
                        showPopover
                          ? "rlr-popover--visible"
                          : "rlr-popover--hide"
                      }`}
                    >
                      <div className="rlr-share">
                        <div className="d-flex justify-content-between align-items-center">
                          {" "}
                          <h3 className="rlr-share__title mb-5">
                            {t("Share with a friend")}{" "}
                          </h3>
                          <Button
                            type="button"
                            onClick={togglePopover}
                            className="btn btn-danger d-block mb-5"
                            style={{ borderRadius: "50%" }}
                          >
                            <FontAwesomeIcon icon={faClose} />
                          </Button>
                        </div>
                        <ul className="rlr-share__items">
                          <li className="rlr-share__list rlr-js--facebook">
                            <a
                              target="_blank"
                              href={`https://ww.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                              className="rlr-icon-text rlr-icon-text--anchor rlr-icon-text__block rlr-share__item"
                            >
                              <i className="rlr-icon-font flaticon-facebook"></i>
                              <span className="rlr-share__title">
                                {t("Facebook")}{" "}
                              </span>
                            </a>
                          </li>
                          <li className="rlr-share__list rlr-js--twitter">
                            <a
                              target="_blank"
                              href={`https://twitter.com/intent/tweet?text=${packageDetails.packageDetails?.pageTitle}&url=${window.location.href}`}
                              className="rlr-icon-text rlr-icon-text--anchor rlr-icon-text__block rlr-share__item social-links"
                            >
                              <span className="link twitter m-auto">
                                <span
                                  style={{
                                    margin: "auto",
                                    textAlign: "center",
                                    width: "35px",
                                    border: "2px solid #5c6770",
                                    height: "35px",
                                    padding: "5px",
                                    display: "flex",
                                    justifyContent: "center ",
                                  }}
                                ></span>
                              </span>
                              <span className="rlr-share__title">
                                {t("Twitter")}{" "}
                              </span>
                            </a>
                          </li>
                          <li className="rlr-share__list rlr-js--whatsapp">
                            <a
                              target="_blank"
                              href={`https://api.whatsapp.com/send?text=${packageDetails.pageDetails?.pageTitle}%20${window.location.href}`}
                              className="rlr-icon-text rlr-icon-text--anchor rlr-icon-text__block rlr-share__item"
                            >
                              <i className="rlr-icon-font flaticon-whatsapp"></i>
                              <span className="rlr-share__title">
                                {t("Whatsapp")}{" "}
                              </span>
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {packageDetails.packagePhotos?.length > 0 && (
                <div className="row mx-auto   justify-content-lg-between px-0">
                  <div
                    className="col-12 col-md-10 m-auto position-relative px-0"
                    style={{ boxShadow: "0 0 5px 2px #eee" }}
                  >
                    <Splide options={mainOptions} onMounted={setMainSplide}>
                      {packageDetails.packagePhotos?.map((image, index) => (
                        <SplideSlide
                          key={index}
                          className="d-flex justify-content-center align-items-center"
                        >
                          <img
                            src={image.photoFileName}
                            alt={`Slide ${index}`}
                            loading="lazy"
                            style={{
                              width: "auto",
                              height: "auto",
                              borderRadius: ".5rem",
                            }}
                          />
                          <div className="rlr-media__custom-pagination rlr-js-custom-pagination">
                            <svg
                              width="18"
                              height="14"
                              viewBox="0 0 18 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1.2 0C.542 0 0 .558 0 1.235v11.53C0 13.442.542 14 1.2 14h15.6c.658 0 1.2-.558 1.2-1.235V1.235C18 .558 17.458 0 16.8 0H1.2zm0 .824h15.6c.228 0 .4.176.4.411v9.844l-3.506-3.95a.4.4 0 0 0-.588 0l-2.862 3.126L6.1 5.488a.4.4 0 0 0-.362-.135.4.4 0 0 0-.232.129L.8 10.687V1.235C.8 1 .972.823 1.2.823zm9.2 2.058c-.879 0-1.6.743-1.6 1.647 0 .905.721 1.647 1.6 1.647.879 0 1.6-.742 1.6-1.647 0-.904-.721-1.647-1.6-1.647zm0 .824c.447 0 .8.363.8.823 0 .46-.353.824-.8.824a.806.806 0 0 1-.8-.824c0-.46.353-.823.8-.823zm-4.606 2.67 5.912 6.8H1.2a.397.397 0 0 1-.4-.411v-.869l4.994-5.52zm7.6 1.64 3.806 4.285v.464a.397.397 0 0 1-.4.411h-4.019l-2-2.303 2.613-2.856z"
                                fill="#212529"
                              ></path>
                            </svg>
                            <span className="rlr-media__page-counter rlr-js-page">
                              {index + 1} /{" "}
                              {packageDetails.packagePhotos?.length}
                            </span>
                          </div>
                        </SplideSlide>
                      ))}
                    </Splide>{" "}
                  </div>
                  {!isMobile && (
                    <div className="col-md-2">
                      <Splide
                        options={thumbnailOptions}
                        onMounted={setThumbnailSplide}
                      >
                        {packageDetails.packagePhotos?.map((image, index) => (
                          <SplideSlide key={index}>
                            <img
                              src={image.photoFileName}
                              alt={`Thumbnail ${index}`}
                              style={{
                                width: "100%",
                                height: "122px",
                                cursor: "pointer",
                                borderRadius: ".5rem",
                              }}
                              loading="lazy"
                            />
                          </SplideSlide>
                        ))}
                      </Splide>
                    </div>
                  )}
                </div>
              )}
            </aside>
          </div>
        </section>
      )}
      <section className="container d-flex  justify-content-between align-items-start flex-wrap rlr-product-detail-section pb-5">
        <div className="rlr-product-detail-section__details col-lg-8">
          <span id="rlr-product-sec-overview-section"></span>
          <nav className="rlr-product-detail-secondary-menu">
            <ul
              className="rlr-product-detail-secondary-menu__tabitems"
              id="rlr-js-secondary-menu"
            >
              <li
                className={`rlr-product-detail-secondary-menu__tabitem js-tabitem ${
                  activeTab === "rlr-product-sec-overview" ? "is-active" : ""
                }`}
                id="rlr-product-sec-overview"
                onClick={() => {
                  setActiveTab("rlr-product-sec-overview");
                  navigate("#rlr-product-sec-overview-section");
                }}
              >
                <span>{t("Overview")}</span>
              </li>
              <li
                className={`rlr-product-detail-secondary-menu__tabitem js-tabitem ${
                  activeTab === "rlr-product-sec-itinerary" ? "is-active" : ""
                }`}
                id="rlr-product-sec-itinerary"
                onClick={() => {
                  setActiveTab("rlr-product-sec-itinerary");
                  navigate("#rlr-product-sec-itinerary-section");
                }}
              >
                <span>{t("Itinerary")}</span>
              </li>
              <li
                className={`rlr-product-detail-secondary-menu__tabitem js-tabitem ${
                  activeTab === "rlr-product-sec-Included" ? "is-active" : ""
                }`}
                id="rlr-product-sec-Included"
                onClick={() => {
                  setActiveTab("rlr-product-sec-Included");
                  navigate("#rlr-product-sec-Included-section");
                }}
              >
                <span>{t("Included")}</span>
              </li>
              <li
                className={`rlr-product-detail-secondary-menu__tabitem js-tabitem ${
                  activeTab === "rlr-product-sec-Excluded" ? "is-active" : ""
                }`}
                id="rlr-product-sec-Excluded"
                onClick={() => {
                  setActiveTab("rlr-product-sec-Excluded");
                  navigate("#rlr-product-sec-Excluded-section");
                }}
              >
                <span>{t("Excluded")}</span>
              </li>
              <li
                className={`rlr-product-detail-secondary-menu__tabitem js-tabitem ${
                  activeTab === "rlr-product-sec-faq" ? "is-active" : ""
                }`}
                id="rlr-product-sec-faq"
                onClick={() => {
                  setActiveTab("rlr-product-sec-faq");
                  navigate("#rlr-product-sec-faq-section");
                }}
              >
                <span>{t("Policies")}</span>
              </li>
            </ul>
          </nav>

          {/* Overview */}

          <div
            className="rlr-secondary-menu-desc"
            data-id="rlr-product-sec-overview"
          >
            {!isMobile && (
              <div className="rlr-secondary-menu-desc__icon">
                <svg
                  width="41"
                  height="51"
                  viewBox="0 0 41 51"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M40.327 13.61H28.296c-.334 0-.558-.221-.558-.55l.002-11.852c0-.329.224-.55.558-.55.334 0 .558.221.558.55l-.002 11.304h11.473c.334 0 .558.22.558.55 0 .33-.224.547-.558.547z"
                    fill="#99A3AD"
                  ></path>
                  <path
                    d="M36.54 50.707H4.568C2.005 50.707 0 48.73 0 46.207L.002 5.047c0-2.525 2.005-4.5 4.568-4.5h23.728c.11 0 .334.109.445.109L40.885 12.51c.11.11.11.22.11.439v33.255c.113 2.527-1.892 4.503-4.455 4.503zM4.568 1.756c-1.892 0-3.342 1.428-3.342 3.292v41.158c0 1.867 1.56 3.402 3.453 3.402H36.65c1.894 0 3.453-1.537 3.453-3.402l.002-32.926-11.92-11.524H4.567z"
                    fill="#99A3AD"
                  ></path>
                  <path
                    d="M33.309 19.756h-19.27c-.335 0-.558-.22-.558-.55 0-.329.223-.549.557-.549h19.273c.334 0 .558.22.558.55-.002.329-.226.55-.56.55zM33.309 25.133H7.91c-.334 0-.558-.22-.558-.55 0-.328.224-.549.558-.549h25.399c.334 0 .558.22.558.55 0 .331-.224.55-.558.55zM33.309 30.622H7.91c-.334 0-.558-.22-.558-.55 0-.329.224-.55.558-.55h25.399c.334 0 .558.221.558.55 0 .33-.224.55-.558.55zM33.309 36.11H7.91c-.334 0-.558-.22-.558-.55 0-.329.224-.549.558-.549h25.399c.334 0 .558.22.558.55 0 .329-.224.55-.558.55zM33.309 41.487H7.91c-.334 0-.558-.22-.558-.549 0-.33.224-.55.558-.55h25.399c.334 0 .558.22.558.55 0 .33-.224.55-.558.55z"
                    fill="#99A3AD"
                  ></path>
                </svg>
              </div>
            )}
            <div className="rlr-secondary-menu-desc__details">
              <div className="rlr-overview-detail">
                <div className="rlr-readmore-desc rlr-overview-detail__description">
                  <h4 className="rlr-section__title--main">
                    {" "}
                    {isMobile && (
                      <div className="rlr-secondary-menu-desc__icon d-inline-block">
                        <svg
                          width="35"
                          height="35"
                          viewBox="0 0 41 51"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M40.327 13.61H28.296c-.334 0-.558-.221-.558-.55l.002-11.852c0-.329.224-.55.558-.55.334 0 .558.221.558.55l-.002 11.304h11.473c.334 0 .558.22.558.55 0 .33-.224.547-.558.547z"
                            fill="#99A3AD"
                          ></path>
                          <path
                            d="M36.54 50.707H4.568C2.005 50.707 0 48.73 0 46.207L.002 5.047c0-2.525 2.005-4.5 4.568-4.5h23.728c.11 0 .334.109.445.109L40.885 12.51c.11.11.11.22.11.439v33.255c.113 2.527-1.892 4.503-4.455 4.503zM4.568 1.756c-1.892 0-3.342 1.428-3.342 3.292v41.158c0 1.867 1.56 3.402 3.453 3.402H36.65c1.894 0 3.453-1.537 3.453-3.402l.002-32.926-11.92-11.524H4.567z"
                            fill="#99A3AD"
                          ></path>
                          <path
                            d="M33.309 19.756h-19.27c-.335 0-.558-.22-.558-.55 0-.329.223-.549.557-.549h19.273c.334 0 .558.22.558.55-.002.329-.226.55-.56.55zM33.309 25.133H7.91c-.334 0-.558-.22-.558-.55 0-.328.224-.549.558-.549h25.399c.334 0 .558.22.558.55 0 .331-.224.55-.558.55zM33.309 30.622H7.91c-.334 0-.558-.22-.558-.55 0-.329.224-.55.558-.55h25.399c.334 0 .558.221.558.55 0 .33-.224.55-.558.55zM33.309 36.11H7.91c-.334 0-.558-.22-.558-.55 0-.329.224-.549.558-.549h25.399c.334 0 .558.22.558.55 0 .329-.224.55-.558.55zM33.309 41.487H7.91c-.334 0-.558-.22-.558-.549 0-.33.224-.55.558-.55h25.399c.334 0 .558.22.558.55 0 .33-.224.55-.558.55z"
                            fill="#99A3AD"
                          ></path>
                        </svg>
                      </div>
                    )}{" "}
                    {t("Highlights")}
                  </h4>
                  <p
                    className="rlr-readmore-desc__content rlr-js-desc"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: !showMore && "3",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: packageDetails.packageDetails?.productIntro,
                    }}
                  />
                  {packageDetails.packageDetails?.productIntro.length > 300 ? (
                    <span
                      className="rlr-readmore-desc__readmore rlr-js-readmore is-more  "
                      onClick={() => setShowMore(!showMore)}
                    >
                      {showMore ? (
                        <Link to="#details"> {t("Show Less")}...</Link>
                      ) : (
                        `${t("Show more")}...`
                      )}
                    </span>
                  ) : isMobile &&
                    packageDetails.packageDetails?.productIntro.length > 150 ? (
                    <span
                      className="rlr-readmore-desc__readmore rlr-js-readmore is-more  "
                      onClick={() => setShowMore(!showMore)}
                    >
                      {showMore ? (
                        <Link to="#details">{t("Show Less")}...</Link>
                      ) : (
                        `${t("Show more")}...`
                      )}
                    </span>
                  ) : (
                    ""
                  )}{" "}
                </div>
                <div className="rlr-overview-detail__icon-groupset">
                  <div className="rlr-overview-detail__icon-group">
                    <span className="rlr-overview-detail__label">
                      {" "}
                      {t("Location")}{" "}
                    </span>
                    <div className="rlr-overview-detail__icons">
                      <figure className="rlr-icon-text">
                        <i className="rlr-icon-font flaticon-map-marker"></i>{" "}
                        <span>
                          {" "}
                          {packageDetails.packageDetails?.destinationName}
                        </span>
                      </figure>
                    </div>
                  </div>
                  <div className="rlr-overview-detail__icon-group">
                    <span className="rlr-overview-detail__label">
                      {t("Duration")}
                    </span>
                    <div className="rlr-overview-detail__icons">
                      <figure className="rlr-icon-text">
                        <i className="rlr-icon-font flaticon-three-o-clock-clock"></i>{" "}
                        <span>
                          {packageDetails.packageDetails?.fullDuration}
                        </span>
                      </figure>
                    </div>
                  </div>
                  <div className="rlr-overview-detail__icon-group">
                    <span className="rlr-overview-detail__label">
                      {t("Run")}
                    </span>
                    <div className="rlr-overview-detail__icons">
                      <figure className="rlr-icon-text">
                        <i className="rlr-icon-font flaticon-calendar"></i>{" "}
                        <span>
                          {packageDetails.packageDetails?.productRunDays}
                        </span>
                      </figure>
                    </div>
                  </div>
                  <div className="rlr-overview-detail__icon-group">
                    <span className="rlr-overview-detail__label">
                      {t("Group Size")}
                    </span>
                    <div className="rlr-overview-detail__icons">
                      <figure className="rlr-icon-text">
                        <i className="rlr-icon-font flaticon-add-user"></i>{" "}
                        <span>
                          {
                            packageDetails.packageDetails
                              ?.productGroupSizeNameSys
                          }
                        </span>
                      </figure>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <span id="rlr-product-sec-itinerary-section"></span>
          {/* Itinerary   */}
          <div
            className="rlr-secondary-menu-desc"
            data-id="rlr-product-sec-itinerary"
          >
            {!isMobile && (
              <div className="rlr-secondary-menu-desc__icon">
                <svg
                  width="43"
                  height="38"
                  viewBox="0 0 43 38"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M43 .442c0-.016-.008-.03-.01-.047-.002-.015.003-.031 0-.047-.003-.014-.013-.024-.018-.037a.372.372 0 0 0-.035-.082.41.41 0 0 0-.177-.172.409.409 0 0 0-.08-.031.397.397 0 0 0-.085-.017C42.581.007 42.568 0 42.553 0c-.018 0-.032.008-.049.01-.016.002-.031-.003-.047 0L28.519 3.04 14.577.01h-.007c-.015-.003-.033 0-.05-.002-.013 0-.024-.008-.039-.008-.013 0-.025.006-.037.008-.018 0-.037 0-.054.003h-.004L.35 3.06c-.013.003-.024.013-.037.017a.44.44 0 0 0-.152.082.389.389 0 0 0-.059.062.438.438 0 0 0-.046.068.459.459 0 0 0-.049.165c0 .014-.008.025-.008.04v34.063c0 .017.008.03.01.047s-.001.03.002.047c.004.016.013.028.018.043a.377.377 0 0 0 .08.141c.014.017.026.033.042.047.026.023.056.04.087.058.017.009.031.02.049.026a.448.448 0 0 0 .255.022l13.94-3.03 13.94 3.03c.032.008.064.011.095.011.005 0 .01-.003.014-.003a.432.432 0 0 0 .082-.008l14.036-3.05c.014-.002.024-.012.037-.017a.47.47 0 0 0 .152-.082.44.44 0 0 0 .056-.06.43.43 0 0 0 .049-.07.432.432 0 0 0 .03-.075.374.374 0 0 0 .018-.089c0-.014.008-.025.008-.04V.443zM.894 3.85 14.037.993v16.584a.446.446 0 0 0-.272.136.44.44 0 0 0 .032.625c.082.073.158.151.24.225v15.589L.894 37.008V3.85zM14.93 19.413c.275.273.544.544.785.792a.448.448 0 0 0 .63.011.44.44 0 0 0 .012-.625 48.965 48.965 0 0 0-1.427-1.407V.993l13.144 2.856v21.217c-.259.11-.517.208-.776.284a.44.44 0 0 0 .125.866.446.446 0 0 0 .126-.019c.174-.051.35-.113.525-.178v10.988l-13.144-2.856V19.413zm14.036 6.215a9.751 9.751 0 0 0 1.137-.678.44.44 0 0 0 .11-.616.448.448 0 0 0-.62-.11 9.369 9.369 0 0 1-.627.398V3.85L42.108.993v33.159l-13.143 2.855V25.628z"
                    fill="#99A3AD"
                  ></path>
                  <path
                    d="M5.452 26.484a26.968 26.968 0 0 1-.167-.966.445.445 0 0 0-.508-.37.442.442 0 0 0-.373.504c.055.352.105.634.147.857a2.365 2.365 0 0 0-1.848 2.297 2.37 2.37 0 0 0 2.378 2.358 2.37 2.37 0 0 0 2.378-2.358c-.001-1.174-.873-2.143-2.007-2.322zM5.08 30.28c-.82 0-1.487-.661-1.487-1.474S4.26 27.33 5.08 27.33c.82 0 1.486.662 1.486 1.475 0 .813-.667 1.474-1.486 1.474zM38.218 6.8l1.051-1.043a.44.44 0 0 0 0-.625.449.449 0 0 0-.63 0l-1.051 1.043-1.05-1.043a.449.449 0 0 0-.631 0 .44.44 0 0 0 0 .625L36.958 6.8l-1.051 1.043a.44.44 0 0 0 .316.755.45.45 0 0 0 .315-.13l.66-.656c.005.106.01.204.013.323.006.24.204.432.445.432h.012a.444.444 0 0 0 .434-.453l-.006-.185.542.539a.45.45 0 0 0 .631 0 .44.44 0 0 0 0-.626L38.22 6.8zM12.178 15.992c-.9-.55-1.764-.9-2.644-1.073a.441.441 0 1 0-.173.867c.773.152 1.542.466 2.35.959a.448.448 0 0 0 .613-.146.44.44 0 0 0-.146-.607zM4.551 23.348l.028-.001a.445.445 0 0 0 .418-.468 21.743 21.743 0 0 1-.042-1.335c0-.433.014-.869.048-1.3a.443.443 0 0 0-.41-.475c-.244-.032-.46.164-.479.406-.036.453-.05.913-.05 1.369 0 .47.015.938.043 1.388.014.235.21.416.444.416zM4.94 17.99a.447.447 0 0 0 .577-.254c.34-.877.834-1.479 1.47-1.788a.44.44 0 0 0 .202-.592.448.448 0 0 0-.596-.202c-.844.411-1.486 1.174-1.908 2.265a.439.439 0 0 0 .255.57zM18.24 21.53a.45.45 0 0 0-.63-.004.44.44 0 0 0-.003.626 27.948 27.948 0 0 0 2.017 1.868.444.444 0 0 0 .628-.057.44.44 0 0 0-.057-.623 27.215 27.215 0 0 1-1.954-1.81zM37.629 10.418a.443.443 0 0 0-.478.41c-.07.942-.19 1.822-.357 2.612a.442.442 0 0 0 .438.533c.206 0 .39-.143.435-.351.175-.828.301-1.746.374-2.729a.446.446 0 0 0-.412-.475zM36.584 15.69a.447.447 0 0 0-.594.209c-.051.105-.104.209-.16.311-.306.561-.686 1.257-1.138 2.015a.44.44 0 0 0 .157.606.45.45 0 0 0 .611-.155c.459-.77.845-1.476 1.155-2.045.062-.115.123-.232.18-.35a.44.44 0 0 0-.211-.59zM33.236 20.46a20.156 20.156 0 0 1-1.677 2.046.441.441 0 0 0 .326.745.446.446 0 0 0 .325-.14c.585-.62 1.174-1.339 1.75-2.137a.44.44 0 0 0-.102-.616.446.446 0 0 0-.622.101zM24.791 25.553a7.526 7.526 0 0 1-2.417-.754.446.446 0 0 0-.631.222.44.44 0 0 0 .21.558l.017.01c.904.448 1.816.731 2.712.842a.445.445 0 0 0 .498-.384.443.443 0 0 0-.389-.494z"
                    fill="#99A3AD"
                  ></path>
                </svg>
              </div>
            )}
            <div className="rlr-secondary-menu-desc__details">
              <div className="rlr-readmore-desc">
                <h4 className="rlr-section__title--main">
                  {" "}
                  {isMobile && (
                    <svg
                      width="35"
                      height="35"
                      viewBox="0 0 43 38"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="m-3"
                    >
                      <path
                        d="M43 .442c0-.016-.008-.03-.01-.047-.002-.015.003-.031 0-.047-.003-.014-.013-.024-.018-.037a.372.372 0 0 0-.035-.082.41.41 0 0 0-.177-.172.409.409 0 0 0-.08-.031.397.397 0 0 0-.085-.017C42.581.007 42.568 0 42.553 0c-.018 0-.032.008-.049.01-.016.002-.031-.003-.047 0L28.519 3.04 14.577.01h-.007c-.015-.003-.033 0-.05-.002-.013 0-.024-.008-.039-.008-.013 0-.025.006-.037.008-.018 0-.037 0-.054.003h-.004L.35 3.06c-.013.003-.024.013-.037.017a.44.44 0 0 0-.152.082.389.389 0 0 0-.059.062.438.438 0 0 0-.046.068.459.459 0 0 0-.049.165c0 .014-.008.025-.008.04v34.063c0 .017.008.03.01.047s-.001.03.002.047c.004.016.013.028.018.043a.377.377 0 0 0 .08.141c.014.017.026.033.042.047.026.023.056.04.087.058.017.009.031.02.049.026a.448.448 0 0 0 .255.022l13.94-3.03 13.94 3.03c.032.008.064.011.095.011.005 0 .01-.003.014-.003a.432.432 0 0 0 .082-.008l14.036-3.05c.014-.002.024-.012.037-.017a.47.47 0 0 0 .152-.082.44.44 0 0 0 .056-.06.43.43 0 0 0 .049-.07.432.432 0 0 0 .03-.075.374.374 0 0 0 .018-.089c0-.014.008-.025.008-.04V.443zM.894 3.85 14.037.993v16.584a.446.446 0 0 0-.272.136.44.44 0 0 0 .032.625c.082.073.158.151.24.225v15.589L.894 37.008V3.85zM14.93 19.413c.275.273.544.544.785.792a.448.448 0 0 0 .63.011.44.44 0 0 0 .012-.625 48.965 48.965 0 0 0-1.427-1.407V.993l13.144 2.856v21.217c-.259.11-.517.208-.776.284a.44.44 0 0 0 .125.866.446.446 0 0 0 .126-.019c.174-.051.35-.113.525-.178v10.988l-13.144-2.856V19.413zm14.036 6.215a9.751 9.751 0 0 0 1.137-.678.44.44 0 0 0 .11-.616.448.448 0 0 0-.62-.11 9.369 9.369 0 0 1-.627.398V3.85L42.108.993v33.159l-13.143 2.855V25.628z"
                        fill="#99A3AD"
                      ></path>
                      <path
                        d="M5.452 26.484a26.968 26.968 0 0 1-.167-.966.445.445 0 0 0-.508-.37.442.442 0 0 0-.373.504c.055.352.105.634.147.857a2.365 2.365 0 0 0-1.848 2.297 2.37 2.37 0 0 0 2.378 2.358 2.37 2.37 0 0 0 2.378-2.358c-.001-1.174-.873-2.143-2.007-2.322zM5.08 30.28c-.82 0-1.487-.661-1.487-1.474S4.26 27.33 5.08 27.33c.82 0 1.486.662 1.486 1.475 0 .813-.667 1.474-1.486 1.474zM38.218 6.8l1.051-1.043a.44.44 0 0 0 0-.625.449.449 0 0 0-.63 0l-1.051 1.043-1.05-1.043a.449.449 0 0 0-.631 0 .44.44 0 0 0 0 .625L36.958 6.8l-1.051 1.043a.44.44 0 0 0 .316.755.45.45 0 0 0 .315-.13l.66-.656c.005.106.01.204.013.323.006.24.204.432.445.432h.012a.444.444 0 0 0 .434-.453l-.006-.185.542.539a.45.45 0 0 0 .631 0 .44.44 0 0 0 0-.626L38.22 6.8zM12.178 15.992c-.9-.55-1.764-.9-2.644-1.073a.441.441 0 1 0-.173.867c.773.152 1.542.466 2.35.959a.448.448 0 0 0 .613-.146.44.44 0 0 0-.146-.607zM4.551 23.348l.028-.001a.445.445 0 0 0 .418-.468 21.743 21.743 0 0 1-.042-1.335c0-.433.014-.869.048-1.3a.443.443 0 0 0-.41-.475c-.244-.032-.46.164-.479.406-.036.453-.05.913-.05 1.369 0 .47.015.938.043 1.388.014.235.21.416.444.416zM4.94 17.99a.447.447 0 0 0 .577-.254c.34-.877.834-1.479 1.47-1.788a.44.44 0 0 0 .202-.592.448.448 0 0 0-.596-.202c-.844.411-1.486 1.174-1.908 2.265a.439.439 0 0 0 .255.57zM18.24 21.53a.45.45 0 0 0-.63-.004.44.44 0 0 0-.003.626 27.948 27.948 0 0 0 2.017 1.868.444.444 0 0 0 .628-.057.44.44 0 0 0-.057-.623 27.215 27.215 0 0 1-1.954-1.81zM37.629 10.418a.443.443 0 0 0-.478.41c-.07.942-.19 1.822-.357 2.612a.442.442 0 0 0 .438.533c.206 0 .39-.143.435-.351.175-.828.301-1.746.374-2.729a.446.446 0 0 0-.412-.475zM36.584 15.69a.447.447 0 0 0-.594.209c-.051.105-.104.209-.16.311-.306.561-.686 1.257-1.138 2.015a.44.44 0 0 0 .157.606.45.45 0 0 0 .611-.155c.459-.77.845-1.476 1.155-2.045.062-.115.123-.232.18-.35a.44.44 0 0 0-.211-.59zM33.236 20.46a20.156 20.156 0 0 1-1.677 2.046.441.441 0 0 0 .326.745.446.446 0 0 0 .325-.14c.585-.62 1.174-1.339 1.75-2.137a.44.44 0 0 0-.102-.616.446.446 0 0 0-.622.101zM24.791 25.553a7.526 7.526 0 0 1-2.417-.754.446.446 0 0 0-.631.222.44.44 0 0 0 .21.558l.017.01c.904.448 1.816.731 2.712.842a.445.445 0 0 0 .498-.384.443.443 0 0 0-.389-.494z"
                        fill="#99A3AD"
                      ></path>
                    </svg>
                  )}{" "}
                  {t("Itinerary")}
                </h4>
              </div>

              <div className="accordion rlr-accordion">
                {packageDetails.productDays?.map((itinary, index) => (
                  <div
                    className="accordion-item rlr-accordion__item"
                    key={index}
                  >
                    <div
                      className="accordion-header rlr-accordion__header"
                      id="rlr-itinerary-header_1"
                    >
                      <button
                        className="accordion-button rlr-accordion__button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#rlr-itinerary-collapse_${index + 1}`}
                        aria-expanded="false"
                        aria-controls={`rlr-itinerary-collapse_${index + 1}`}
                      >
                        <span className="rlr-accordion__badge">
                          {index + 1}{" "}
                        </span>{" "}
                        {t("Day")} {itinary.dayNumber} - {itinary.dayTitle}
                      </button>
                    </div>
                    <div
                      id={`rlr-itinerary-collapse_${index + 1}`}
                      className="accordion-collapse collapse show"
                      aria-labelledby="rlr-itinerary-header_1"
                    >
                      <div className="accordion-body rlr-accordion__body">
                        <div className="rlr-readmore-desc">
                          <p
                            className="rlr-readmore-desc__content rlr-js-desc"
                            dangerouslySetInnerHTML={{
                              __html: itinary.dayDetails,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <span id="rlr-product-sec-Included-section"></span>
          {/* Included   */}
          <div
            className="rlr-secondary-menu-desc"
            data-id="rlr-product-sec-Included"
          >
            {!isMobile && (
              <div className="rlr-secondary-menu-desc__icon">
                <svg
                  width="50"
                  height="56"
                  viewBox="0 0 50 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M25.0157 0.127686H24.9679C21.6869 0.129864 18.4385 0.787955 15.4081 2.06438C12.3776 3.34081 9.62461 5.21058 7.30612 7.56694C4.98764 9.92329 3.14911 12.7201 1.89553 15.7976C0.641942 18.8752 -0.00215716 22.1732 5.42768e-06 25.5034C0.000252218 25.6667 0.0590139 25.8244 0.165333 25.9469C0.271652 26.0695 0.41827 26.1487 0.577845 26.1696C0.737421 26.1906 0.89906 26.1519 1.03262 26.0609C1.16618 25.9698 1.26255 25.8325 1.30374 25.6746C1.47986 24.998 2.10654 23.0634 2.89022 22.2039C3.99055 21.1594 5.33307 20.6056 6.77668 20.6172C8.20636 20.6136 9.57983 21.182 10.5995 22.1992C11.6258 23.3104 12.1684 24.6663 12.1684 26.1199C12.1684 26.2984 12.2382 26.4695 12.3626 26.5957C12.4869 26.7219 12.6555 26.7928 12.8313 26.7928C13.0071 26.7928 13.1758 26.7219 13.3001 26.5957C13.4244 26.4695 13.4942 26.2984 13.4942 26.1199C13.4942 24.6681 14.035 23.3147 15.058 22.2046C16.1518 21.1661 17.4853 20.6173 18.9187 20.6173C19.6312 20.6115 20.3379 20.7482 20.9984 21.0196C21.6589 21.2911 22.2602 21.6919 22.768 22.1993C23.7943 23.3105 24.3369 24.6665 24.3369 26.1199V44.326C24.3369 45.5699 23.8501 46.7629 22.9835 47.6424C22.1169 48.522 20.9416 49.0162 19.7161 49.0162C18.4905 49.0162 17.3152 48.522 16.4486 47.6424C15.582 46.7629 15.0952 45.5699 15.0952 44.326V42.1116C15.0952 41.9331 15.0253 41.762 14.901 41.6358C14.7767 41.5096 14.6081 41.4387 14.4323 41.4387C14.2565 41.4387 14.0878 41.5096 13.9635 41.6358C13.8392 41.762 13.7694 41.9331 13.7694 42.1116V44.326C13.7694 45.9268 14.3959 47.4621 15.5111 48.594C16.6263 49.7259 18.1389 50.3619 19.7161 50.3619C21.2932 50.3619 22.8058 49.7259 23.921 48.594C25.0362 47.4621 25.6628 45.9268 25.6628 44.326V26.1199C25.6628 24.6679 26.2034 23.3148 27.2265 22.2051C28.3203 21.1667 29.6536 20.6179 31.0872 20.6179H31.1138C32.5435 20.6143 33.917 21.1828 34.9366 22.2C35.9629 23.3111 36.5055 24.6665 36.5055 26.1206C36.5055 26.2991 36.5754 26.4702 36.6997 26.5964C36.824 26.7226 36.9926 26.7935 37.1685 26.7935C37.3443 26.7935 37.5129 26.7226 37.6372 26.5964C37.7615 26.4702 37.8314 26.2991 37.8314 26.1206C37.8314 24.6689 38.372 23.3155 39.3952 22.2052C40.489 21.1667 41.8224 20.618 43.2557 20.618H43.2824C44.7121 20.6144 46.0856 21.1827 47.1053 22.1999C47.8914 23.0567 48.5195 24.9971 48.6963 25.6745C48.7375 25.8324 48.8338 25.9697 48.9674 26.0608C49.101 26.1518 49.2626 26.1905 49.4222 26.1695C49.5818 26.1486 49.7284 26.0694 49.8347 25.9468C49.941 25.8242 49.9998 25.6665 50 25.5032V25.4707C49.9958 18.7479 47.3617 12.3019 42.6767 7.5496C37.9917 2.79732 31.6392 0.127744 25.0157 0.127686ZM48.0616 21.268C48.0558 21.2616 48.0497 21.2553 48.0435 21.2491C46.7765 19.9788 45.0662 19.2682 43.2853 19.272H43.2522C41.4716 19.272 39.8184 19.9522 38.4714 21.239C38.4617 21.2483 38.4522 21.258 38.443 21.2678C37.9254 21.8213 37.4952 22.4528 37.168 23.1398C36.841 22.4529 36.4112 21.8214 35.8938 21.268C35.8878 21.2616 35.8817 21.2552 35.8757 21.2491C34.6086 19.9788 32.8983 19.2682 31.1173 19.272H31.0842C29.3035 19.272 27.6503 19.9522 26.3035 21.239C26.2937 21.2483 26.2843 21.2579 26.2752 21.2677C25.7574 21.8211 25.3272 22.4525 24.9998 23.1395C24.6726 22.4525 24.2425 21.821 23.7248 21.2675C23.7189 21.2611 23.7129 21.2548 23.7067 21.2487C22.4397 19.9784 20.7294 19.2678 18.9485 19.2716H18.9154C17.1347 19.2716 15.4816 19.9517 14.1344 21.2386C14.1247 21.2479 14.1152 21.2575 14.106 21.2674C13.5885 21.8209 13.1586 22.4525 12.8314 23.1395C12.5042 22.4524 12.0741 21.821 11.5564 21.2675C11.5505 21.2611 11.5445 21.2548 11.5383 21.2487C10.2714 19.9785 8.56139 19.2678 6.78066 19.2715H6.74751C4.9668 19.2715 3.3136 19.9516 1.96655 21.2385C1.95683 21.2478 1.94733 21.2574 1.93816 21.2674C1.82613 21.3891 1.72132 21.5174 1.62426 21.6517C2.52389 16.0239 5.36592 10.9042 9.64244 7.20767C13.9189 3.51112 19.3512 1.47861 24.9683 1.47341H24.9998C25.0051 1.47273 25.0105 1.47273 25.0157 1.47341C30.6357 1.47473 36.0719 3.50557 40.3517 7.20259C44.6315 10.8996 47.4758 16.0216 48.3756 21.6523C48.2785 21.518 48.1737 21.3896 48.0616 21.268Z"
                    fill="#99A3AD"
                  ></path>
                  <path
                    d="M49.6287 34.0793L39.8939 29.2311C39.8032 29.1859 39.7034 29.1624 39.6023 29.1624C39.5011 29.1624 39.4014 29.1859 39.3106 29.2311L29.5758 34.0655C29.4644 34.1209 29.3705 34.2069 29.3048 34.3137C29.2391 34.4206 29.2043 34.544 29.2043 34.67V43.919C29.2043 44.9461 29.4805 46.0925 30.0031 47.2341C30.6726 48.6962 31.6698 49.9592 32.739 50.6977L39.2287 55.1857C39.3387 55.2618 39.4688 55.3025 39.6019 55.3025C39.7351 55.3025 39.8651 55.2618 39.9751 55.1857L46.465 50.6976C48.447 49.3275 49.9997 46.3499 49.9997 43.9189V34.6834C49.9997 34.5576 49.9649 34.4343 49.8993 34.3275C49.8337 34.2207 49.74 34.1347 49.6287 34.0793ZM48.6738 43.9187C48.6738 45.8829 47.3205 48.4779 45.7186 49.5853L39.6019 53.8151L33.4852 49.5853C32.6078 48.9787 31.777 47.915 31.2054 46.6666C30.7699 45.7153 30.53 44.7394 30.53 43.9191V35.0895L39.6009 30.5845L48.6733 35.1027L48.6738 43.9187Z"
                    fill="#99A3AD"
                  ></path>
                  <path
                    d="M35.9457 41.4619C35.8196 41.3385 35.6505 41.2707 35.4754 41.2734C35.3002 41.2761 35.1332 41.349 35.0109 41.4763C34.8885 41.6036 34.8208 41.7749 34.8225 41.9527C34.8242 42.1305 34.8952 42.3004 35.0199 42.4253L37.9605 45.3362C38.0844 45.4587 38.2505 45.5274 38.4234 45.5274C38.5964 45.5274 38.7625 45.4587 38.8863 45.3362L44.1839 40.0917C44.3086 39.9668 44.3796 39.7969 44.3813 39.6191C44.383 39.4413 44.3153 39.27 44.193 39.1427C44.0706 39.0154 43.9036 38.9425 43.7285 38.9398C43.5533 38.9371 43.3842 39.0049 43.2581 39.1284L38.4239 43.9146L35.9457 41.4619Z"
                    fill="#99A3AD"
                  ></path>
                </svg>
              </div>
            )}

            <div className="rlr-secondary-menu-desc__details">
              <div className="rlr-readmore-desc">
                <h4 className="rlr-section__title--main">
                  {" "}
                  {isMobile && (
                    <div className="rlr-secondary-menu-desc__icon d-inline pb-5">
                      <svg
                        width="50"
                        height="55"
                        viewBox="0 0 50 56"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M25.0157 0.127686H24.9679C21.6869 0.129864 18.4385 0.787955 15.4081 2.06438C12.3776 3.34081 9.62461 5.21058 7.30612 7.56694C4.98764 9.92329 3.14911 12.7201 1.89553 15.7976C0.641942 18.8752 -0.00215716 22.1732 5.42768e-06 25.5034C0.000252218 25.6667 0.0590139 25.8244 0.165333 25.9469C0.271652 26.0695 0.41827 26.1487 0.577845 26.1696C0.737421 26.1906 0.89906 26.1519 1.03262 26.0609C1.16618 25.9698 1.26255 25.8325 1.30374 25.6746C1.47986 24.998 2.10654 23.0634 2.89022 22.2039C3.99055 21.1594 5.33307 20.6056 6.77668 20.6172C8.20636 20.6136 9.57983 21.182 10.5995 22.1992C11.6258 23.3104 12.1684 24.6663 12.1684 26.1199C12.1684 26.2984 12.2382 26.4695 12.3626 26.5957C12.4869 26.7219 12.6555 26.7928 12.8313 26.7928C13.0071 26.7928 13.1758 26.7219 13.3001 26.5957C13.4244 26.4695 13.4942 26.2984 13.4942 26.1199C13.4942 24.6681 14.035 23.3147 15.058 22.2046C16.1518 21.1661 17.4853 20.6173 18.9187 20.6173C19.6312 20.6115 20.3379 20.7482 20.9984 21.0196C21.6589 21.2911 22.2602 21.6919 22.768 22.1993C23.7943 23.3105 24.3369 24.6665 24.3369 26.1199V44.326C24.3369 45.5699 23.8501 46.7629 22.9835 47.6424C22.1169 48.522 20.9416 49.0162 19.7161 49.0162C18.4905 49.0162 17.3152 48.522 16.4486 47.6424C15.582 46.7629 15.0952 45.5699 15.0952 44.326V42.1116C15.0952 41.9331 15.0253 41.762 14.901 41.6358C14.7767 41.5096 14.6081 41.4387 14.4323 41.4387C14.2565 41.4387 14.0878 41.5096 13.9635 41.6358C13.8392 41.762 13.7694 41.9331 13.7694 42.1116V44.326C13.7694 45.9268 14.3959 47.4621 15.5111 48.594C16.6263 49.7259 18.1389 50.3619 19.7161 50.3619C21.2932 50.3619 22.8058 49.7259 23.921 48.594C25.0362 47.4621 25.6628 45.9268 25.6628 44.326V26.1199C25.6628 24.6679 26.2034 23.3148 27.2265 22.2051C28.3203 21.1667 29.6536 20.6179 31.0872 20.6179H31.1138C32.5435 20.6143 33.917 21.1828 34.9366 22.2C35.9629 23.3111 36.5055 24.6665 36.5055 26.1206C36.5055 26.2991 36.5754 26.4702 36.6997 26.5964C36.824 26.7226 36.9926 26.7935 37.1685 26.7935C37.3443 26.7935 37.5129 26.7226 37.6372 26.5964C37.7615 26.4702 37.8314 26.2991 37.8314 26.1206C37.8314 24.6689 38.372 23.3155 39.3952 22.2052C40.489 21.1667 41.8224 20.618 43.2557 20.618H43.2824C44.7121 20.6144 46.0856 21.1827 47.1053 22.1999C47.8914 23.0567 48.5195 24.9971 48.6963 25.6745C48.7375 25.8324 48.8338 25.9697 48.9674 26.0608C49.101 26.1518 49.2626 26.1905 49.4222 26.1695C49.5818 26.1486 49.7284 26.0694 49.8347 25.9468C49.941 25.8242 49.9998 25.6665 50 25.5032V25.4707C49.9958 18.7479 47.3617 12.3019 42.6767 7.5496C37.9917 2.79732 31.6392 0.127744 25.0157 0.127686ZM48.0616 21.268C48.0558 21.2616 48.0497 21.2553 48.0435 21.2491C46.7765 19.9788 45.0662 19.2682 43.2853 19.272H43.2522C41.4716 19.272 39.8184 19.9522 38.4714 21.239C38.4617 21.2483 38.4522 21.258 38.443 21.2678C37.9254 21.8213 37.4952 22.4528 37.168 23.1398C36.841 22.4529 36.4112 21.8214 35.8938 21.268C35.8878 21.2616 35.8817 21.2552 35.8757 21.2491C34.6086 19.9788 32.8983 19.2682 31.1173 19.272H31.0842C29.3035 19.272 27.6503 19.9522 26.3035 21.239C26.2937 21.2483 26.2843 21.2579 26.2752 21.2677C25.7574 21.8211 25.3272 22.4525 24.9998 23.1395C24.6726 22.4525 24.2425 21.821 23.7248 21.2675C23.7189 21.2611 23.7129 21.2548 23.7067 21.2487C22.4397 19.9784 20.7294 19.2678 18.9485 19.2716H18.9154C17.1347 19.2716 15.4816 19.9517 14.1344 21.2386C14.1247 21.2479 14.1152 21.2575 14.106 21.2674C13.5885 21.8209 13.1586 22.4525 12.8314 23.1395C12.5042 22.4524 12.0741 21.821 11.5564 21.2675C11.5505 21.2611 11.5445 21.2548 11.5383 21.2487C10.2714 19.9785 8.56139 19.2678 6.78066 19.2715H6.74751C4.9668 19.2715 3.3136 19.9516 1.96655 21.2385C1.95683 21.2478 1.94733 21.2574 1.93816 21.2674C1.82613 21.3891 1.72132 21.5174 1.62426 21.6517C2.52389 16.0239 5.36592 10.9042 9.64244 7.20767C13.9189 3.51112 19.3512 1.47861 24.9683 1.47341H24.9998C25.0051 1.47273 25.0105 1.47273 25.0157 1.47341C30.6357 1.47473 36.0719 3.50557 40.3517 7.20259C44.6315 10.8996 47.4758 16.0216 48.3756 21.6523C48.2785 21.518 48.1737 21.3896 48.0616 21.268Z"
                          fill="#99A3AD"
                        ></path>
                        <path
                          d="M49.6287 34.0793L39.8939 29.2311C39.8032 29.1859 39.7034 29.1624 39.6023 29.1624C39.5011 29.1624 39.4014 29.1859 39.3106 29.2311L29.5758 34.0655C29.4644 34.1209 29.3705 34.2069 29.3048 34.3137C29.2391 34.4206 29.2043 34.544 29.2043 34.67V43.919C29.2043 44.9461 29.4805 46.0925 30.0031 47.2341C30.6726 48.6962 31.6698 49.9592 32.739 50.6977L39.2287 55.1857C39.3387 55.2618 39.4688 55.3025 39.6019 55.3025C39.7351 55.3025 39.8651 55.2618 39.9751 55.1857L46.465 50.6976C48.447 49.3275 49.9997 46.3499 49.9997 43.9189V34.6834C49.9997 34.5576 49.9649 34.4343 49.8993 34.3275C49.8337 34.2207 49.74 34.1347 49.6287 34.0793ZM48.6738 43.9187C48.6738 45.8829 47.3205 48.4779 45.7186 49.5853L39.6019 53.8151L33.4852 49.5853C32.6078 48.9787 31.777 47.915 31.2054 46.6666C30.7699 45.7153 30.53 44.7394 30.53 43.9191V35.0895L39.6009 30.5845L48.6733 35.1027L48.6738 43.9187Z"
                          fill="#99A3AD"
                        ></path>
                        <path
                          d="M35.9457 41.4619C35.8196 41.3385 35.6505 41.2707 35.4754 41.2734C35.3002 41.2761 35.1332 41.349 35.0109 41.4763C34.8885 41.6036 34.8208 41.7749 34.8225 41.9527C34.8242 42.1305 34.8952 42.3004 35.0199 42.4253L37.9605 45.3362C38.0844 45.4587 38.2505 45.5274 38.4234 45.5274C38.5964 45.5274 38.7625 45.4587 38.8863 45.3362L44.1839 40.0917C44.3086 39.9668 44.3796 39.7969 44.3813 39.6191C44.383 39.4413 44.3153 39.27 44.193 39.1427C44.0706 39.0154 43.9036 38.9425 43.7285 38.9398C43.5533 38.9371 43.3842 39.0049 43.2581 39.1284L38.4239 43.9146L35.9457 41.4619Z"
                          fill="#99A3AD"
                        ></path>
                      </svg>
                    </div>
                  )}
                  {t("Included")}
                </h4>
              </div>

              <div
                className="rlr-readmore-desc"
                dangerouslySetInnerHTML={{
                  __html: packageDetails.packageDetails?.productIncluded,
                }}
              />
            </div>
          </div>
          <span id="rlr-product-sec-Excluded-section"></span>
          {/* Excluded   */}
          <div
            className="rlr-secondary-menu-desc"
            data-id="rlr-product-sec-Excluded"
          >
            {!isMobile && (
              <div className="rlr-secondary-menu-desc__icon">
                <img
                  src="images/Icons/umbrella_exclude_icon.png"
                  style={{ minWidth: "50px", filter: "contrast(0.2)" }}
                  alt="Excloded icon"
                />
              </div>
            )}

            <div className="rlr-secondary-menu-desc__details">
              <div className="rlr-readmore-desc">
                <h3 className="rlr-section__title--main">
                  {" "}
                  {isMobile && (
                    <div className="rlr-secondary-menu-desc__icon d-inline  ">
                      <img
                        src="images/Icons/umbrella_exclude_icon.png"
                        style={{ minWidth: "50px", filter: "contrast(0.2)" }}
                        alt="Excloded icon"
                      />
                    </div>
                  )}
                  {t("Excluded")}
                </h3>
              </div>
              <div
                className="rlr-readmore-desc"
                dangerouslySetInnerHTML={{
                  __html: packageDetails.packageDetails?.productExcluded,
                }}
              />
            </div>
          </div>
          <span id="rlr-product-sec-faq-section"></span>
          {/* Policies  */}
          <div
            className="rlr-secondary-menu-desc"
            data-id="rlr-product-sec-faq"
          >
            <div className="rlr-secondary-menu-desc__icon">
              <svg
                width="52"
                height="54"
                viewBox="0 0 52 54"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M47.9971 40.8774C51.3711 35.5059 52.6762 29.0842 51.6677 22.8163C50.6593 16.5484 47.4065 10.8647 42.5192 6.8308C37.6319 2.79691 31.4458 0.689826 25.1205 0.904586C18.7953 1.11935 12.7653 3.6412 8.16107 7.99736C3.55685 12.3535 0.694579 18.2448 0.110868 24.5668C-0.472843 30.8887 1.26209 37.2072 4.99041 42.3376C8.71873 47.468 14.1844 51.0581 20.3627 52.4347C26.541 53.8113 33.0077 52.8799 38.5504 49.8151L49.3317 52.6147C49.6365 52.693 49.9567 52.6867 50.2582 52.5963C50.5596 52.506 50.8309 52.3351 51.0428 52.1019C51.2548 51.8687 51.3995 51.582 51.4614 51.2726C51.5234 50.9632 51.5002 50.6427 51.3944 50.3455L47.9971 40.8774ZM38.6544 48.0328C38.4348 47.9753 38.2014 48.0065 38.0044 48.1198C32.8405 51.0625 26.7801 52.0034 20.971 50.7642C15.162 49.525 10.0076 46.1917 6.48404 41.3957C2.96052 36.5996 1.31253 30.6739 1.85219 24.7406C2.39186 18.8074 5.08169 13.2787 9.41227 9.2016C13.7429 5.12451 19.4135 2.78212 25.3502 2.61806C31.2869 2.454 37.0775 4.47967 41.6252 8.31141C46.173 12.1432 49.1622 17.5149 50.0266 23.4094C50.891 29.3038 49.5707 35.3116 46.3158 40.2949C46.2423 40.4065 46.1955 40.5336 46.1789 40.6663C46.1624 40.799 46.1766 40.9337 46.2204 41.06L49.7651 50.9367L38.6544 48.0328Z"
                  fill="#99A3AD"
                ></path>
                <path
                  d="M25.5503 34.2444C24.8943 34.2199 24.2553 34.4575 23.7737 34.9051C23.5472 35.1221 23.3685 35.3843 23.2491 35.6748C23.1297 35.9652 23.0722 36.2775 23.0803 36.5916C23.0679 36.9042 23.1226 37.2158 23.2407 37.5053C23.3589 37.7947 23.5377 38.0554 23.765 38.2695C24.267 38.6903 24.9004 38.9209 25.5547 38.9209C26.2089 38.9209 26.8423 38.6903 27.3443 38.2695C27.5665 38.0555 27.7415 37.7972 27.858 37.5112C27.9744 37.2252 28.0297 36.9178 28.0203 36.609C28.0285 36.2949 27.971 35.9826 27.8516 35.6922C27.7322 35.4017 27.5535 35.1395 27.327 34.9225C27.0905 34.697 26.8116 34.5209 26.5066 34.4045C26.2016 34.288 25.8765 34.2336 25.5503 34.2444Z"
                  fill="#99A3AD"
                ></path>
                <path
                  d="M30.872 17.7334C29.4476 16.6114 27.6615 16.0543 25.854 16.1684C24.0226 16.0615 22.2183 16.6514 20.8014 17.8203C20.1747 18.3996 19.6812 19.1086 19.355 19.8983C19.0287 20.688 18.8776 21.5394 18.912 22.3935H23.332C23.3148 21.6853 23.5692 20.9976 24.0427 20.4721C24.2777 20.2352 24.5595 20.0502 24.8699 19.929C25.1803 19.8077 25.5126 19.7528 25.8453 19.7678C27.4313 19.7678 28.2287 20.6373 28.2287 22.3761C28.2256 22.9526 28.0668 23.5174 27.7693 24.0106C27.2536 24.7605 26.6351 25.4336 25.932 26.0103C25.117 26.6886 24.4678 27.5451 24.034 28.5142C23.6467 29.6069 23.4702 30.7635 23.514 31.9224H27.414L27.4747 30.9921C27.5919 29.9917 28.0623 29.0664 28.8007 28.3838L30.04 27.2014C30.854 26.4594 31.5393 25.5867 32.068 24.6192C32.4542 23.8572 32.6533 23.0136 32.6487 22.1587C32.6975 21.3352 32.5635 20.511 32.2562 19.7457C31.949 18.9804 31.4762 18.2931 30.872 17.7334V17.7334Z"
                  fill="#99A3AD"
                ></path>
              </svg>
            </div>
            <div className="rlr-secondary-menu-desc__details">
              <div className="accordion rlr-accordion">
                {packageDetails?.packageDetails?.productCancellationPolicy && (
                  <div className="accordion-item rlr-accordion__item">
                    <div
                      className="accordion-header rlr-accordion__header"
                      id="rlr-faq-collapse-header2"
                    >
                      <button
                        className="accordion-button rlr-accordion__button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#rlr-faq-collapse2"
                        aria-expanded="false"
                        aria-controls="rlr-faq-collapse2"
                      >
                        <span className="rlr-accordion__badge">? </span>{" "}
                        {t("Cancellation Policy")}
                      </button>
                    </div>
                    <div
                      id="rlr-faq-collapse2"
                      className="accordion-collapse collapse"
                      aria-labelledby="rlr-faq-collapse-header2"
                    >
                      <div className="accordion-body rlr-accordion__body">
                        <div
                          className="rlr-readmore-desc"
                          dangerouslySetInnerHTML={{
                            __html:
                              packageDetails.packageDetails
                                ?.productCancellationPolicy,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {packageDetails.packageDetails
                  ?.productEmergencyCancellationPolicy && (
                  <div className="accordion-item rlr-accordion__item">
                    <div
                      className="accordion-header rlr-accordion__header"
                      id="rlr-faq-collapse-header6"
                    >
                      <button
                        className="accordion-button rlr-accordion__button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#rlr-faq-collapse6"
                        aria-expanded="false"
                        aria-controls="rlr-faq-collapse6"
                      >
                        <span className="rlr-accordion__badge">? </span>
                        {t("Emergency Cancellation Policy")}
                      </button>
                    </div>
                    <div
                      id="rlr-faq-collapse6"
                      className="accordion-collapse collapse"
                      aria-labelledby="rlr-faq-collapse-header6"
                    >
                      <div className="accordion-body rlr-accordion__body">
                        <div
                          className="rlr-readmore-desc"
                          dangerouslySetInnerHTML={{
                            __html:
                              packageDetails.packageDetails
                                ?.productEmergencyCancellationPolicy,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {packageDetails.packageDetails?.productChildrenPolicy && (
                  <div className="accordion-item rlr-accordion__item">
                    <div
                      className="accordion-header rlr-accordion__header"
                      id="rlr-faq-collapse-header66"
                    >
                      <button
                        className="accordion-button rlr-accordion__button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#rlr-faq-collapse66"
                        aria-expanded="false"
                        aria-controls="rlr-faq-collapse66"
                      >
                        <span className="rlr-accordion__badge">? </span>
                        {t("Children Policy")}
                      </button>
                    </div>
                    <div
                      id="rlr-faq-collapse66"
                      className="accordion-collapse collapse"
                      aria-labelledby="rlr-faq-collapse-header66"
                    >
                      <div className="accordion-body rlr-accordion__body">
                        <div
                          className="rlr-readmore-desc"
                          dangerouslySetInnerHTML={{
                            __html:
                              packageDetails.packageDetails
                                ?.productChildrenPolicy,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {packageDetails.packageDetails?.productWhattoBring &&
                  packageDetails.packageDetails?.productWhattoBring !=
                    "   " && (
                    <div className="accordion-item rlr-accordion__item">
                      <div
                        className="accordion-header rlr-accordion__header"
                        id="rlr-faq-collapse-header77"
                      >
                        <button
                          className="accordion-button rlr-accordion__button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#rlr-faq-collapse77"
                          aria-expanded="false"
                          aria-controls="rlr-faq-collapse77"
                        >
                          <span className="rlr-accordion__badge">? </span>{" "}
                          {t("What To Bring")}
                        </button>
                      </div>
                      <div
                        id="rlr-faq-collapse77"
                        className="accordion-collapse collapse"
                        aria-labelledby="rlr-faq-collapse-header77"
                      >
                        <div className="accordion-body rlr-accordion__body">
                          <div
                            className="rlr-readmore-desc"
                            dangerouslySetInnerHTML={{
                              __html:
                                packageDetails.packageDetails
                                  ?.productWhattoBring,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
              </div>
              {packageDetails.packageDetails?.productNote && (
                <div className="accordion-item rlr-accordion__item">
                  <div
                    className="accordion-header rlr-accordion__header"
                    id="rlr-faq-collapse-header7"
                  >
                    <button
                      className="accordion-button rlr-accordion__button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#rlr-faq-collapse7"
                      aria-expanded="false"
                      aria-controls="rlr-faq-collapse7"
                    >
                      <span className="rlr-accordion__badge">? </span>{" "}
                      {t("Note")}
                    </button>
                  </div>
                  <div
                    id="rlr-faq-collapse7"
                    className="accordion-collapse collapse"
                    aria-labelledby="rlr-faq-collapse-header7"
                  >
                    <div className="accordion-body rlr-accordion__body">
                      <div
                        className="rlr-readmore-desc"
                        dangerouslySetInnerHTML={{
                          __html: packageDetails.packageDetails?.productNote,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Booking Engine  */}
        <aside className="col-12 col-lg-4 col-xxxl-3 d-xl-block offset-xxxl-1 mt-5 mt-lg-0 px-lg-4">
          <form className="col-12" onSubmit={handleSubmit}>
            <fieldset className="rlr-fieldrow mt-0">
              <article
                itemScope
                itemType="https://schema.org/Offer"
                className="rlr-booking-card__price-details rlr-fieldrow__form-element"
              >
                <span className="rlr-booking-card__total-price">
                  {t("Total price")}
                </span>{" "}
                <header className="rlr-booking-card__offer">
                  <h2
                    className="rlr-booking-card__price"
                    itemScope
                    itemType="https://schema.org/AggregateOffer"
                  >
                    <span
                      className="rlr-booking-card__current-price rlr-booking-card--currency"
                      itemProp="priceCurrency"
                    />
                    <span
                      id="Price"
                      itemProp="lowPrice"
                      className="rlr-booking-card__current-price rlr-booking-card--low-price"
                    >
                      {packageDetails.packageDetails?.priceCurrency}
                      {packagePrice}
                    </span>
                    <span
                      itemProp="category"
                      className="rlr-booking-card__price-type rlr-booking-card__price-type--result"
                    >
                      {t("inc. taxes")}
                    </span>
                  </h2>
                  <svg width="56" height="57" viewBox="0 0 56 57" fill="none">
                    <rect
                      y=".411"
                      width="56"
                      height="56"
                      rx="28"
                      fill="#28B0A6"
                    ></rect>
                    <path
                      d="M24.253 40.365a1.629 1.629 0 0 1-2.31 0l-9.225-9.226a2.45 2.45 0 0 1 0-3.466l1.155-1.155a2.45 2.45 0 0 1 3.466 0l5.76 5.76L38.66 16.715a2.45 2.45 0 0 1 3.466 0l1.155 1.155a2.45 2.45 0 0 1 0 3.465l-19.029 19.03z"
                      fill="#fff"
                    ></path>
                  </svg>
                </header>
              </article>
              <div className="rlr-fieldrow__item rlr-booking-card__form-item">
                <label
                  className="rlr-form-label rlr-form-label--dark rlr-booking-card__label"
                  htmlFor="rlr-booking-dateranges-input"
                >
                  {t("Dates")}
                </label>
                <div
                  className="rlr-input-group rlr-input-group__datefield rlr-js-booking-form-daterange"
                  id="rlr-booking-startRange"
                >
                  {packageDetails.packageDetails?.isGroupDeparture ? (
                    <select
                      id="RoomType"
                      name="RoomType"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                      }}
                    >
                      {packageDetails.packageDetails.fixedGroupAvailableDates?.map(
                        (date) => (
                          <option key={date} value={formatDate(date)}>
                            {formatDate(date)}
                          </option>
                        )
                      )}
                    </select>
                  ) : (
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => {
                        setStartDate(date);
                      }}
                      className="form-control form-input"
                      placeholderText="Select preferred dates"
                      minDate={
                        !packageDetails.packageDetails?.isGroupDeparture &&
                        addDays(
                          new Date(),
                          packageDetails.packageDetails?.cutOfDays
                        )
                      }
                      maxDate={
                        !packageDetails.packageDetails?.isGroupDeparture && end1
                      }
                      filterDate={isDateAvailable}
                      locale={getLocale()}
                    />
                  )}
                </div>
              </div>
              <div className="rlr-fieldrow__item rlr-booking-card__form-item">
                <div className="d-flex justify-content-between col-12 mb-3">
                  <div className="rlr-fieldrow_item rlr-booking-card_form-item col-5 d-flex flex-column">
                    <label className="rlr-form-label rlr-form-label--dark rlr-booking-card__label">
                      {t("Room Type")}
                    </label>
                    <select
                      id="RoomType"
                      name="RoomType"
                      className="form-control"
                      value={roomType}
                      onChange={(e) => setRoomType(e.target.value)}
                    >
                      <option value="1">{t("Single Room")}</option>
                      <option value="2">{t("Double Room")}</option>
                    </select>
                  </div>
                  <div className="rlr-fieldrow_item rlr-booking-card_form-item col-5 d-flex flex-column">
                    <label className="rlr-form-label rlr-form-label--dark rlr-booking-card__label">
                      {t("Room Numbers")}
                    </label>
                    <select
                      id="RoomNumber"
                      name="RoomNumber"
                      className="form-control"
                      value={roomNumber}
                      onChange={(e) => setRoomNumber(e.target.value)}
                    >
                      <option value="1">1 {t("Room")}</option>
                      <option value="2">2 {t("Rooms")}</option>
                      <option value="3">3 {t("Rooms")}</option>
                      <option value="4">4 {t("Rooms")}</option>
                      <option value="5">5 {t("Rooms")}</option>
                      <option value="6">6 {t("Rooms")}</option>
                      <option value="7">7 {t("Rooms")}</option>
                      <option value="8">8 {t("Rooms")}</option>
                      <option value="9">9 {t("Rooms")}</option>
                      <option value="10">10 {t("Rooms")}</option>
                    </select>
                  </div>
                </div>
                {!productUrl.includes("honeymoon") && (
                  <div className="d-flex justify-content-between col-12 mb-3">
                    <div className="rlr-fieldrow_item rlr-booking-card_form-item col-5 d-flex flex-column">
                      <label className="rlr-form-label rlr-form-label--dark rlr-booking-card__label">
                        {" "}
                        {t("Child")} ({t("Age")}:2-5.99){" "}
                      </label>

                      <select
                        id="ChildrenValue1"
                        name="NumberOfchildrens1"
                        className="form-control"
                        value={numberOfchildrens1}
                        onChange={(e) => setNumberOfchildrens1(e.target.value)}
                      >
                        <option value="0">0</option>
                        <option value="1">1 {t("Child")}</option>
                        <option value="2">2 {t("Child")}</option>
                      </select>
                    </div>
                    <div className="rlr-fieldrow_item rlr-booking-card_form-item col-5 d-flex flex-column">
                      <label className="rlr-form-label rlr-form-label--dark rlr-booking-card__label">
                        {t("Child")} ({t("Age")}:6-11.99)
                      </label>

                      <select
                        id="ChildrenValue2"
                        name="NumberOfchildrens2"
                        className="form-control"
                        value={numberOfchildrens2}
                        onChange={(e) => setNumberOfchildrens2(e.target.value)}
                      >
                        <option value="0">0</option>
                        <option value="1">1 {t("Child")} </option>
                        <option value="2">2 {t("Child")} </option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </fieldset>
            <button
              type="submit"
              className="btn rlr-button rlr-booking-card__button"
            >
              {t("Proceed to Booking")}
            </button>
          </form>
        </aside>
      </section>
    </>
  );
};

export default PackageDetails;
